import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import { requestNativeLocalNotificationPermission } from "../notifications/localNotifications";
import {
  buildNotificationEndpointRegisterOperation,
  buildNotificationEndpointSetActiveOperation,
  type NotificationDevicePlatform,
} from "../operations/notificationDeviceOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { ConfirmationSheet } from "./ConfirmationSheet";

interface NotificationDeviceSettingsPanelProps {
  farmId: string;
  userId: string;
  onSyncSummary?: (summary: OutboxSummary) => void;
}

interface EndpointRow {
  endpointId: string;
  deviceId: string;
  platform: NotificationDevicePlatform;
  provider: string;
  isActive: boolean;
  tokenRegistered: boolean;
  lastSeenAt: string;
  updatedAt: string;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record) : [];
}

function platformValue(value: unknown): NotificationDevicePlatform {
  return value === "android" || value === "ios" ? value : "web";
}

function platformLabel(value: NotificationDevicePlatform): string {
  if (value === "android") return "Android";
  if (value === "ios") return "iOS";
  return "Web";
}

function providerLabel(value: string): string {
  if (value === "local") return "اعلان محلی";
  if (value === "fcm") return "FCM";
  if (value === "apns") return "APNs";
  if (value === "web_push") return "Web Push";
  return value;
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "نامشخص";
  return date.toLocaleString("fa-IR-u-ca-persian", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function syncMessage(status: string): string {
  if (status === "synced") return "تنظیم اعلان دستگاه با سرور همگام شد.";
  if (status === "pending") return "تغییر اعلان در صف آفلاین ثبت شد.";
  if (status === "retryable_error") return "تغییر حفظ شد و دوباره ارسال می‌شود.";
  if (status === "terminal_conflict") return "ثبت تنظیم اعلان با وضعیت سرور تعارض دارد.";
  return "تغییر اعلان ثبت شد.";
}

async function loadEndpoints(farmId: string): Promise<EndpointRow[]> {
  const result = await supabase.rpc("notification_endpoint_self_list_v1", {
    target_farm_id: farmId,
  });

  if (result.error) throw result.error;

  return rows(result.data).map((item) => ({
    endpointId: String(item.endpoint_id),
    deviceId: String(item.device_id ?? ""),
    platform: platformValue(item.platform),
    provider: String(item.provider ?? ""),
    isActive: item.is_active === true,
    tokenRegistered: item.token_registered === true,
    lastSeenAt: String(item.last_seen_at ?? ""),
    updatedAt: String(item.updated_at ?? ""),
  }));
}

export function NotificationDeviceSettingsPanel({
  farmId,
  userId,
  onSyncSummary,
}: NotificationDeviceSettingsPanelProps) {
  const deviceId = useMemo(() => getOrCreateDeviceId(), []);
  const platform = platformValue(Capacitor.getPlatform());

  const [endpoints, setEndpoints] = useState<EndpointRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState<OfflineOperation>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      setEndpoints(await loadEndpoints(farmId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری دستگاه‌های اعلان انجام نشد.");
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const currentLocalEndpoint = useMemo(
    () =>
      endpoints.find(
        (endpoint) =>
          endpoint.deviceId === deviceId &&
          endpoint.platform === platform &&
          endpoint.provider === "local",
      ),
    [deviceId, endpoints, platform],
  );

  async function prepareEnableLocal() {
    setError(undefined);
    setMessage(undefined);

    const permission = await requestNativeLocalNotificationPermission();
    if (permission === "unsupported") {
      setError("اعلان محلی این بخش فعلاً فقط در نسخه native Android و iOS فعال می‌شود.");
      return;
    }
    if (permission === "denied") {
      setError("مجوز اعلان در تنظیمات دستگاه داده نشده است.");
      return;
    }

    try {
      const operation = buildNotificationEndpointRegisterOperation(
        {
          actorId: userId,
          farmId,
          deviceId,
          performedAt: new Date().toISOString(),
        },
        {
          endpointId: currentLocalEndpoint?.endpointId ?? crypto.randomUUID(),
          platform,
          provider: "local",
        },
      );

      setPending(operation);
      setConfirmationParts([
        "فعال‌سازی اعلان محلی",
        `دستگاه: ${deviceId}`,
        `پلتفرم: ${platformLabel(platform)}`,
        "توکن push ذخیره یا نمایش داده نمی‌شود.",
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "فعال‌سازی اعلان آماده نشد.");
    }
  }

  function prepareSetActive(endpoint: EndpointRow, isActive: boolean) {
    setError(undefined);
    setMessage(undefined);

    try {
      const operation = buildNotificationEndpointSetActiveOperation(
        {
          actorId: userId,
          farmId,
          deviceId,
          performedAt: new Date().toISOString(),
        },
        {
          endpointId: endpoint.endpointId,
          isActive,
        },
      );

      setPending(operation);
      setConfirmationParts([
        isActive ? "فعال‌سازی endpoint" : "غیرفعال‌سازی endpoint",
        `پلتفرم: ${platformLabel(endpoint.platform)}`,
        `ارائه‌دهنده: ${providerLabel(endpoint.provider)}`,
        `دستگاه: ${endpoint.deviceId}`,
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "تغییر وضعیت endpoint آماده نشد.");
    }
  }

  async function confirmPending() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);
      const result = await persistAndSyncOperation(
        supabase,
        pending,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );
      onSyncSummary?.(result.summary);
      setMessage(syncMessage(result.operation.status));
      setPending(undefined);

      if (result.operation.status === "synced") {
        await refresh();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت تنظیم اعلان انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section-card" aria-labelledby="notification-device-settings-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">M4C2</p>
          <h2 id="notification-device-settings-title">اعلان‌ها و دستگاه</h2>
        </div>
        <span className="count-pill">{endpoints.filter((item) => item.isActive).length} فعال</span>
      </div>

      <p className="section-note">
        این بخش همان موتور اعلان C12 را استفاده می‌کند. push token خصوصی است و در این صفحه یا Data
        API نمایش داده نمی‌شود.
      </p>

      <div className="manager-card">
        <p className="section-kicker">این دستگاه</p>
        <h3>{platformLabel(platform)}</h3>
        <p className="section-note">شناسه دستگاه: {deviceId}</p>
        <p className="section-note">
          وضعیت اعلان محلی: {currentLocalEndpoint?.isActive ? "فعال" : "غیرفعال یا ثبت‌نشده"}
        </p>

        <button
          type="button"
          className="secondary-button"
          disabled={busy || loading || currentLocalEndpoint?.isActive === true}
          onClick={() => void prepareEnableLocal()}
        >
          فعال‌سازی اعلان محلی این دستگاه
        </button>

        {currentLocalEndpoint?.isActive ? (
          <button
            type="button"
            className="text-button"
            disabled={busy}
            onClick={() => prepareSetActive(currentLocalEndpoint, false)}
          >
            غیرفعال‌سازی اعلان این دستگاه
          </button>
        ) : null}
      </div>

      <div className="work-list">
        {endpoints.map((endpoint) => {
          const isCurrent = endpoint.deviceId === deviceId && endpoint.platform === platform;
          return (
            <div className="work-item pending" key={endpoint.endpointId}>
              <span className="work-main">
                <strong>
                  {platformLabel(endpoint.platform)} · {providerLabel(endpoint.provider)}
                  {isCurrent ? " · این دستگاه" : ""}
                </strong>
                <span>
                  {endpoint.isActive ? "فعال" : "غیرفعال"} · آخرین مشاهده{" "}
                  {formatDate(endpoint.lastSeenAt)}
                </span>
                <span>توکن remote: {endpoint.tokenRegistered ? "ثبت شده و مخفی" : "ثبت نشده"}</span>
              </span>
              {endpoint.isActive ? (
                <button
                  type="button"
                  className="text-button"
                  disabled={busy}
                  onClick={() => prepareSetActive(endpoint, false)}
                >
                  غیرفعال
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {loading ? <p className="section-note">در حال بارگذاری دستگاه‌ها…</p> : null}
      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید تنظیم اعلان"
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirmPending()}
        />
      ) : null}
    </section>
  );
}
